import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Save, Clock, RefreshCw, Bell, Mail, Plus, X, Tag, CalendarRange } from "lucide-react";

export default function ReglasConfig() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nuevoEmail, setNuevoEmail] = useState("");

  useEffect(() => {
    api
      .get("/settings")
      .then(({ data }) => setSettings({ ...data, emails_operaciones: data.emails_operaciones || [] }))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  const agregarEmail = () => {
    const e = nuevoEmail.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) return toast.error("Email inválido.");
    if (settings.emails_operaciones.includes(e)) return toast.error("Ese correo ya está en la lista.");
    setSettings({ ...settings, emails_operaciones: [...settings.emails_operaciones, e] });
    setNuevoEmail("");
  };

  const quitarEmail = (e) => {
    setSettings({ ...settings, emails_operaciones: settings.emails_operaciones.filter((x) => x !== e) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        politica_cancelacion_default_horas: parseInt(settings.politica_cancelacion_default_horas, 10),
        polling_minutos: parseInt(settings.polling_minutos, 10),
        alerta_ocupacion_pct: parseInt(settings.alerta_ocupacion_pct, 10),
        emails_operaciones: settings.emails_operaciones,
        etiqueta_procesado: (settings.etiqueta_procesado || "").trim() || "Procesado App",
        agente_minutos_para_pagar:
          settings.agente_minutos_para_pagar === "" || settings.agente_minutos_para_pagar === undefined
            ? null
            : parseInt(settings.agente_minutos_para_pagar, 10),
        // PROMPT 107: el recordatorio previo al tour.
        agente_recordatorio_horas:
          settings.agente_recordatorio_horas === "" || settings.agente_recordatorio_horas === undefined
            ? null
            : parseInt(settings.agente_recordatorio_horas, 10),
        agente_recordatorio_a_todos: !!settings.agente_recordatorio_a_todos,
        // PROMPT 117: "" = sin limite.
        portal_reservas_desde: settings.portal_reservas_desde || "",
      });
      toast.success("Reglas guardadas.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center justify-center h-40">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <Card className="p-6 space-y-5">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Clock className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Política de cancelación por defecto (horas)</Label>
            <Input
              type="number"
              value={settings.politica_cancelacion_default_horas}
              onChange={(e) => setSettings({ ...settings, politica_cancelacion_default_horas: e.target.value })}
              className="max-w-[160px]"
              data-testid="setting-cancelacion-input"
            />
            <p className="text-xs text-slate-500">
              Mínimo de horas antes de la salida para aceptar una cancelación. Se puede sobrescribir por tour.
            </p>
          </div>
        </div>

        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <RefreshCw className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Frecuencia de lectura de correos (minutos)</Label>
            <Input
              type="number"
              value={settings.polling_minutos}
              onChange={(e) => setSettings({ ...settings, polling_minutos: e.target.value })}
              className="max-w-[160px]"
              data-testid="setting-polling-input"
            />
            <p className="text-xs text-slate-500">Frecuencia con la que el sistema lee los buzones configurados en Configuración → Buzones.</p>
          </div>
        </div>

        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Bell className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Umbral de alerta de ocupación (%)</Label>
            <Input
              type="number"
              value={settings.alerta_ocupacion_pct}
              onChange={(e) => setSettings({ ...settings, alerta_ocupacion_pct: e.target.value })}
              className="max-w-[160px]"
              data-testid="setting-ocupacion-input"
            />
            <p className="text-xs text-slate-500">
              Alerta visual (amarillo) al alcanzar este porcentaje. Al 100% se marca en rojo.
            </p>
          </div>
        </div>
        {/* PROMPT 92 (2026-09-04): plazo de pago de las reservas que toma el agente de WhatsApp. */}
        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Bell className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Minutos para pagar una reserva de WhatsApp</Label>
            <Input
              type="number"
              value={settings.agente_minutos_para_pagar ?? 30}
              onChange={(e) => setSettings({ ...settings, agente_minutos_para_pagar: e.target.value })}
              className="max-w-[160px]"
              data-testid="setting-agente-minutos-pago"
            />
            <p className="text-xs text-slate-500">
              El agente le guarda el lugar al cliente mientras paga. Pasados estos minutos sin pago, la reserva se
              cancela sola y el cupo vuelve a quedar disponible. Poné 0 si preferís que no se libere nada
              automáticamente.
            </p>
          </div>
        </div>

        {/* PROMPT 107: recordatorio previo al tour por WhatsApp */}
        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Clock className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Recordatorio del tour por WhatsApp (horas antes)</Label>
            <Input
              type="number"
              value={settings.agente_recordatorio_horas ?? 24}
              onChange={(e) => setSettings({ ...settings, agente_recordatorio_horas: e.target.value })}
              className="max-w-[160px]"
              data-testid="setting-agente-recordatorio-horas"
            />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={!!settings.agente_recordatorio_a_todos}
                onChange={(e) => setSettings({ ...settings, agente_recordatorio_a_todos: e.target.checked })}
                data-testid="setting-agente-recordatorio-todos"
              />
              Mandárselo también a quienes nunca nos escribieron por WhatsApp
            </label>
            <p className="text-xs text-slate-500">
              El agente le recuerda al pasajero su excursión con esta antelación, en su idioma, con el punto de
              encuentro. Poné 0 para que no mande ninguno. Por defecto solo le escribe a quienes ya nos
              escribieron por WhatsApp: Meta exige que el cliente haya aceptado recibir mensajes, y escribirle a
              números que nunca lo hicieron puede hacer que Meta limite el número.
            </p>
          </div>
        </div>
        {/* PROMPT 117: desde que fecha reservan las agencias por el Portal */}
        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <CalendarRange className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Portal de agencias: reservas a partir del</Label>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={settings.portal_reservas_desde || ""}
                onChange={(e) => setSettings({ ...settings, portal_reservas_desde: e.target.value })}
                className="max-w-[200px]"
                data-testid="setting-portal-reservas-desde"
              />
              {settings.portal_reservas_desde ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSettings({ ...settings, portal_reservas_desde: "" })}
                  data-testid="setting-portal-reservas-desde-quitar"
                >
                  Quitar fecha
                </Button>
              ) : null}
            </div>
            <p className="text-xs text-slate-500">
              Las agencias no pueden reservar por el Portal fechas anteriores a esta, ni pasar una reserva a esas
              fechas, ni cargarlas por archivo: el Portal les muestra la disponibilidad desde esta fecha y les avisa
              que las fechas anteriores se reservan con Tours NY como hasta ahora. Lo que ya está cargado no cambia, y
              el equipo puede cargar cualquier fecha desde la app. Vacío = sin límite.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Mail className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Avisos de overbooking (correos de operaciones)</Label>
            <p className="text-xs text-slate-500">
              Cuando una salida llega al 100%, además de la alerta in-app se notificará por email a estas
              direcciones. El envío de email se activará cuando conectemos el transporte (Resend); por ahora la
              alerta in-app funciona en tiempo real.
            </p>
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="operaciones@toursny.com"
                value={nuevoEmail}
                onChange={(e) => setNuevoEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), agregarEmail())}
                data-testid="email-operaciones-input"
                className="max-w-xs"
              />
              <Button type="button" variant="outline" onClick={agregarEmail} data-testid="email-operaciones-add" className="gap-1">
                <Plus className="h-4 w-4" /> Agregar
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 pt-1" data-testid="emails-operaciones-list">
              {(settings.emails_operaciones || []).length === 0 ? (
                <span className="text-xs text-slate-400">Sin destinatarios cargados todavía.</span>
              ) : (
                settings.emails_operaciones.map((e) => (
                  <span
                    key={e}
                    className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs text-slate-700"
                    data-testid={`email-chip-${e}`}
                  >
                    {e}
                    <button onClick={() => quitarEmail(e)} className="text-slate-400 hover:text-red-500" data-testid={`email-remove-${e}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="flex items-start gap-3 border-t border-slate-100 pt-5">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Tag className="h-5 w-5" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Etiqueta de Gmail para correos procesados</Label>
            <Input
              type="text"
              value={settings.etiqueta_procesado ?? "Cargado en App"}
              onChange={(e) => setSettings({ ...settings, etiqueta_procesado: e.target.value })}
              className="max-w-xs"
              placeholder="Cargado en App"
              data-testid="setting-etiqueta-input"
            />
            <p className="text-xs text-slate-500">
              La app <span className="font-medium text-slate-700">no marca los correos como leídos</span>: les
              aplica esta etiqueta PROPIA en Gmail (sin sacarlos de la bandeja) para que el equipo distinga los
              ya tomados por el sistema. Usá un nombre distinto al de cualquier etiqueta que usen a mano (p. ej.
              "Procesado - Interviajes APP" es del equipo). Gmail crea la etiqueta automáticamente si no existe.
            </p>
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button onClick={save} disabled={saving} data-testid="save-settings-button" className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Guardar reglas
          </Button>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 shrink-0">
            <Mail className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-slate-900">Plantillas de respuesta automática</p>
            <p className="text-sm text-slate-500">
              Las plantillas bilingües (es/en) para confirmaciones, rechazos y el protocolo de GetYourGuide se
              editan en la pestaña <span className="font-medium text-slate-700">Plantillas</span>.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
